
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type FlameShaderUniforms = {
	uViewProjection: WebGLUniformLocation,
	uFlamePosition: WebGLUniformLocation,
	uRight: WebGLUniformLocation,
	uWidth: WebGLUniformLocation,
	uHeight: WebGLUniformLocation,
	uTime: WebGLUniformLocation,
	uIntensity: WebGLUniformLocation,
	uBaseColor: WebGLUniformLocation,
	uMidColor: WebGLUniformLocation,
	uTipColor: WebGLUniformLocation,
	uScrollSpeed: WebGLUniformLocation,
	uTaper: WebGLUniformLocation,
	uNoiseScale: WebGLUniformLocation,
	uSeed: WebGLUniformLocation,
	uCameraPosition: WebGLUniformLocation,
	uInvViewProjection: WebGLUniformLocation,
	uSceneDepth: WebGLUniformLocation,
	uScreenSize: WebGLUniformLocation,
	uSoftness: WebGLUniformLocation
};

export type FlameShaderAttributes = {
	aLocalPos: number,
	vLocalUV: number,
	vWorldPos: number
};

export type FlameShaderSettings = string[];

export class FlameShader extends ShaderBase<FlameShaderUniforms, FlameShaderAttributes> {

    private static fragmentShaderSource = `
// Procedural flame body. No texture assets. Domain-warped FBM with strong
// vertical anisotropy gives upward-licking tongues; a density threshold
// punches holes in the body so it reads as multiple curling tongues rather
// than a uniform blob; a high-frequency ember layer near the tip adds
// detached hotspots; a hot-core highlight biases the bright middle toward
// near-white the way real fire does.
//
// Output is premultiplied (color * alpha, alpha) but FlamesPass binds an
// additive blend (\`ONE\`, \`ONE\`) so only \`rgb\` is summed into the target —
// alpha is written for completeness / debug only.
//
// Soft particle: when \`uSoftness > 0\` the alpha is faded as the flame
// fragment approaches the opaque scene depth, so a flame poking through a
// candle's wax or a campfire log doesn't show a hard intersection seam.

// --- begin: noise.glsl ---
// Shared 2D noise primitives — hash, value noise, and FBM.
// Kept deterministic and cheap; matches the formulas already used by
// procedural-sky so behaviour carries over to any shader that includes this.

float hash21(vec2 p) {
	p = fract(p * vec2(123.34, 456.21));
	p += dot(p, p + 45.32);
	return fract(p.x * p.y);
}

float valueNoise2(vec2 p) {
	vec2 i = floor(p);
	vec2 f = fract(p);
	vec2 u = f * f * (3.0 - 2.0 * f);
	float a = hash21(i);
	float b = hash21(i + vec2(1.0, 0.0));
	float c = hash21(i + vec2(0.0, 1.0));
	float d = hash21(i + vec2(1.0, 1.0));
	return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

// 5-octave FBM normalised to roughly [0, 1).
float fbm2(vec2 p) {
	float v = 0.0;
	float a = 0.5;
	float total = 0.0;
	for (int i = 0; i < 5; i++) {
		v += a * valueNoise2(p);
		total += a;
		p = p * 2.02 + vec2(11.7, 3.4);
		a *= 0.5;
	}
	return v / total;
}

// --- end: noise.glsl ---
// --- begin: world_pos_from_depth.glsl ---
// Reconstruct world / view position from a 24-bit depth sample, screen-space
// UV, and an inverse projection matrix. Replaces an explicit RGBA32F position
// attachment in the G-buffer (~16 bytes/pixel saved).
//
// Reversed-Z: the depth buffer maps near → 1.0 and far → 0.0. Cleared depth
// (= 0.0) means "no geometry was drawn" — callers should discard or treat this
// as background BEFORE calling these functions. The reconstruction itself
// still works at depth == 0.0 (it returns the far-plane point), but the
// result is rarely useful for lighting/AO/decals.
//
// The math is reversal-agnostic: invViewProjection is the inverse of whichever
// projection the depth buffer was rendered with, and the perspective divide
// recovers the correct world position regardless of which side of the NDC z
// range each plane maps to.

// Inputs:
//   depth              — raw [0,1] value from texture(uDepthMap, uv).r
//   uv                 — screen-space UV (vTexCoord, in [0,1])
//   invViewProjection  — inverse of (projection * view) for the current camera
vec3 worldPosFromDepth(float depth, vec2 uv, mat4 invViewProjection) {
	vec4 ndc = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
	vec4 world = invViewProjection * ndc;
	return world.xyz / world.w;
}

// View-space variant — pass invProjection (inverse of camera.projectionMatrix)
// when you only need view space. Skips the worldPos → viewPos round-trip,
// which matters for SSAO that samples 16+ positions per fragment.
vec3 viewPosFromDepth(float depth, vec2 uv, mat4 invProjection) {
	vec4 ndc = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
	vec4 view = invProjection * ndc;
	return view.xyz / view.w;
}

// --- end: world_pos_from_depth.glsl ---

// Local-space body half-width. The vertex shader rasterises a quad that
// extends horizontally to \`vLocalUV.x = ±QUAD_PAD\` and the body math below
// uses ±BODY_HALF inside that window — the extra slack gives the wobble +
// domain-warp room to bulge sideways without clipping at the quad edge.
const float BODY_HALF = 0.5;

in vec2 vLocalUV;
in vec3 vWorldPos;
out vec4 fragColor;

uniform float uTime;
uniform float uIntensity;
uniform vec3 uBaseColor;
uniform vec3 uMidColor;
uniform vec3 uTipColor;
uniform float uScrollSpeed;
uniform float uTaper;
uniform float uNoiseScale;
uniform float uSeed;

uniform vec3 uCameraPosition;
uniform mat4 uInvViewProjection;
uniform sampler2D uSceneDepth;
uniform vec2 uScreenSize;
uniform float uSoftness;

void main() {
	float u = vLocalUV.x;
	float v = clamp(vLocalUV.y, 0.0, 1.0);

	float t = uTime * uScrollSpeed;

	// Domain-warp coordinates: a coarse FBM displaces the detail-noise UV.
	// Without warping, FBM reads as straight vertical streaks; warping curls
	// them into the licking-tongue motion of real fire. The warp itself is
	// also vertically anisotropic so it scrolls upward in lockstep with the
	// detail noise — otherwise the warp drifts independently and the flame
	// looks like it's being blown by two different winds.
	vec2 warpUv = vec2(u * 1.6 + uSeed, v * 0.7 - t * 0.55);
	float wx = fbm2(warpUv) - 0.5;
	float wy = fbm2(warpUv + vec2(13.4, 7.1)) - 0.5;

	// Detail noise — strong vertical stretch (×0.4 on v) so a single noise
	// cell occupies a tall vertical band, reading as a "lick" not a "blob".
	vec2 nuv = vec2(
		u * uNoiseScale + wx * 0.6 + uSeed,
		v * uNoiseScale * 0.4 - t + wy * 0.5
	);
	float n = fbm2(nuv);

	// Silhouette: pinch the base (so the flame looks anchored at a wick
	// rather than blob-spreading at the bottom) and taper the tip via
	// \`(1-v)^taper\`. The product gives the classic almond/teardrop shape of
	// a real flame — narrow at base, fattest in the lower-middle, sharp at
	// tip. BASE_PINCH is the fraction of the flame height over which the
	// base ramps from 0 to full width.
	const float BASE_PINCH = 0.18;
	float basePinch = smoothstep(0.0, BASE_PINCH, v);
	float halfWidth = BODY_HALF * basePinch * pow(max(1.0 - v, 0.0), uTaper);
	float wobble = (n - 0.5) * 0.30 * (1.0 - v);
	float inside = halfWidth - abs(u + wobble);
	float silhouette = smoothstep(0.0, 0.08, inside);
	silhouette *= smoothstep(0.0, 0.04, 1.0 - v);
	if (silhouette <= 0.0) discard;

	// Density threshold: drop every fragment whose noise value is below a
	// rising threshold. Low at the base (mostly solid) → high at the tip
	// (almost everything cuts out). The smoothstep band gives soft edges
	// rather than aliased holes.
	float threshold = mix(0.20, 0.62, v);
	float density = smoothstep(threshold, threshold + 0.22, n) * silhouette;

	// Ember layer: high-frequency noise + hard threshold, gated to the upper
	// half of the flame. Produces detached bright spots that float above the
	// main body — the visual signature of soot particles glowing as they
	// rise. The ember must respect the silhouette so embers don't appear in
	// empty quad space.
	vec2 emberUv = vec2(u * 7.5 + uSeed * 1.73, v * 5.5 - t * 1.45);
	float ember = fbm2(emberUv);
	float emberMask = smoothstep(0.62, 0.72, ember)
		* smoothstep(0.42, 0.85, v)
		* smoothstep(0.0, 0.02, silhouette);
	density = max(density, emberMask * 0.65);
	density = clamp(density, 0.0, 1.0);

	// Color: blue base → yellow mid → orange tip.
	vec3 col = mix(uBaseColor, uMidColor, smoothstep(0.0, 0.30, v));
	col = mix(col, uTipColor, smoothstep(0.45, 0.95, v));

	// Hot-core highlight: punch the brightest mid-flame fragments toward a
	// saturated yellow-orange. NOT pushed to white — that would read as a
	// gas torch / propane flame, not a wood/wax fire. The amplitude is
	// deliberately partial (×0.65 mix) so the deep-orange midColor still
	// shows through and the body keeps its warm tone instead of blowing
	// out.
	vec3 hotCore = vec3(1.45, 0.85, 0.18);
	float coreFactor = smoothstep(0.50, 0.90, density)
		* smoothstep(0.08, 0.40, v)
		* (1.0 - smoothstep(0.65, 1.00, v));
	col = mix(col, hotCore, coreFactor * 0.65);

	float alpha = density;

	// Soft-particle fade against scene depth.
	if (uSoftness > 0.0) {
		vec2 screenUv = gl_FragCoord.xy / uScreenSize;
		float sceneDepth = texture(uSceneDepth, screenUv).r;
		// Reversed-Z: cleared depth = 0 means "background, no occluder".
		// Skip the fade in that case so flames against the sky stay solid.
		if (sceneDepth > 0.0) {
			vec3 sceneWorld = worldPosFromDepth(sceneDepth, screenUv, uInvViewProjection);
			float sceneDist = distance(sceneWorld, uCameraPosition);
			float flameDist = distance(vWorldPos, uCameraPosition);
			float diff = sceneDist - flameDist;
			alpha *= clamp(diff / uSoftness, 0.0, 1.0);
		}
	}

	vec3 emissive = col * uIntensity * alpha;
	fragColor = vec4(emissive, alpha);
}
`;

    private static vertexShaderSource = `
// Cylindrical billboard for a single flame tongue. Quad in local space
// extends to x ∈ [-QUAD_PAD, QUAD_PAD], y ∈ [0, 1] (base→tip, anchor at base
// center). The body math in the fragment shader uses x ∈ [-0.5, 0.5] for the
// silhouette, so the extra horizontal slack (\`QUAD_PAD - 0.5\`) gives the
// wobble + domain-warp room to bulge sideways without clipping at the quad
// edge. The CPU computes a horizontal \`right\` vector that rotates the quad
// to face the camera around the world-up axis only — flames are gravity-
// aligned, so a full spherical billboard would roll the flame when the
// camera tilts. World-up is hard-coded as +Y to match the engine's Y-up
// convention.

layout(location = 0) in vec2 aLocalPos;

out vec2 vLocalUV;
out vec3 vWorldPos;

uniform mat4 uViewProjection;
uniform vec3 uFlamePosition;
uniform vec3 uRight;
uniform float uWidth;
uniform float uHeight;

void main() {
	vLocalUV = aLocalPos;
	vec3 worldPos = uFlamePosition
		+ aLocalPos.x * uWidth * uRight
		+ aLocalPos.y * uHeight * vec3(0.0, 1.0, 0.0);
	vWorldPos = worldPos;
	gl_Position = uViewProjection * vec4(worldPos, 1.0);
}
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: FlameShaderUniformsContainer;
    public attributesContainer: FlameShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: FlameShaderSettings) {
        super(ctx, FlameShader.fragmentShaderSource, FlameShader.vertexShaderSource, settings);
        this.uniformsContainer = new FlameShaderUniformsContainer(ctx, this);
        this.attributesContainer = new FlameShaderShaderAttributesContainer(ctx, this);
    }
}

class FlameShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: FlameShader) {
        super(ctx);
    }

    public setViewProjection(value: mat4 | number[]) : FlameShaderUniformsContainer {
        this.setValue('uViewProjection', this.shader.uniforms.uViewProjection, value, 'mat4');
        return this;
    }

    public setFlamePosition(value: vec3 | number[]) : FlameShaderUniformsContainer {
        this.setValue('uFlamePosition', this.shader.uniforms.uFlamePosition, value, 'vec3');
        return this;
    }

    public setRight(value: vec3 | number[]) : FlameShaderUniformsContainer {
        this.setValue('uRight', this.shader.uniforms.uRight, value, 'vec3');
        return this;
    }

    public setWidth(value: number) : FlameShaderUniformsContainer {
        this.setValue('uWidth', this.shader.uniforms.uWidth, value, 'float');
        return this;
    }

    public setHeight(value: number) : FlameShaderUniformsContainer {
        this.setValue('uHeight', this.shader.uniforms.uHeight, value, 'float');
        return this;
    }

    public setTime(value: number) : FlameShaderUniformsContainer {
        this.setValue('uTime', this.shader.uniforms.uTime, value, 'float');
        return this;
    }

    public setIntensity(value: number) : FlameShaderUniformsContainer {
        this.setValue('uIntensity', this.shader.uniforms.uIntensity, value, 'float');
        return this;
    }

    public setBaseColor(value: vec3 | number[]) : FlameShaderUniformsContainer {
        this.setValue('uBaseColor', this.shader.uniforms.uBaseColor, value, 'vec3');
        return this;
    }

    public setMidColor(value: vec3 | number[]) : FlameShaderUniformsContainer {
        this.setValue('uMidColor', this.shader.uniforms.uMidColor, value, 'vec3');
        return this;
    }

    public setTipColor(value: vec3 | number[]) : FlameShaderUniformsContainer {
        this.setValue('uTipColor', this.shader.uniforms.uTipColor, value, 'vec3');
        return this;
    }

    public setScrollSpeed(value: number) : FlameShaderUniformsContainer {
        this.setValue('uScrollSpeed', this.shader.uniforms.uScrollSpeed, value, 'float');
        return this;
    }

    public setTaper(value: number) : FlameShaderUniformsContainer {
        this.setValue('uTaper', this.shader.uniforms.uTaper, value, 'float');
        return this;
    }

    public setNoiseScale(value: number) : FlameShaderUniformsContainer {
        this.setValue('uNoiseScale', this.shader.uniforms.uNoiseScale, value, 'float');
        return this;
    }

    public setSeed(value: number) : FlameShaderUniformsContainer {
        this.setValue('uSeed', this.shader.uniforms.uSeed, value, 'float');
        return this;
    }

    public setCameraPosition(value: vec3 | number[]) : FlameShaderUniformsContainer {
        this.setValue('uCameraPosition', this.shader.uniforms.uCameraPosition, value, 'vec3');
        return this;
    }

    public setInvViewProjection(value: mat4 | number[]) : FlameShaderUniformsContainer {
        this.setValue('uInvViewProjection', this.shader.uniforms.uInvViewProjection, value, 'mat4');
        return this;
    }

    public setSceneDepth(texture: Texture) : FlameShaderUniformsContainer {
        this.setTextureValue('uSceneDepth', this.shader.uniforms.uSceneDepth, texture, 'sampler2D');
        return this;
    }

    public setScreenSize(value: vec2 | number[]) : FlameShaderUniformsContainer {
        this.setValue('uScreenSize', this.shader.uniforms.uScreenSize, value, 'vec2');
        return this;
    }

    public setSoftness(value: number) : FlameShaderUniformsContainer {
        this.setValue('uSoftness', this.shader.uniforms.uSoftness, value, 'float');
        return this;
    }

    protected getTextureUniforms() {
        return [
			{ name: 'uSceneDepth', uniform: this.shader.uniforms.uSceneDepth, dataType: 'sampler2D' }
        ];
    }
}

class FlameShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: FlameShader) {
        super(ctx);
    }

    public setLocalPos(settings: { length: number, offset: number, itemSize: number }) : FlameShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aLocalPos, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvLocalUV(settings: { length: number, offset: number, itemSize: number }) : FlameShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vLocalUV, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvWorldPos(settings: { length: number, offset: number, itemSize: number }) : FlameShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vWorldPos, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}